import React, { useEffect, useState } from "react";
import { Select, SelectItem, TextArea, TextInput } from "@carbon/react";
import i18next from "../../../i18n";
import {
  PurchaseRequestItemType,
  Currency,
  CURRENCY_OPTIONS,
  MaterialResponse,
} from "../../../types";
import { PurchaseRequestFilePayload } from "../../../types/purchaseRequestV2.type";
import { MEASUREMENT_UNIT_LIST } from "../../../utils";
import MaterialAutocompleteCell from "../MaterialAutocompleteCell";
import ItemFilePicker from "./ItemFilePicker";
import type { ItemRow } from "./types";

type SiteOption = { id: string; name: string };

export type ItemDetailProps = {
  item: ItemRow;
  index: number;
  todayString: string;
  sites: SiteOption[];
  onItemChange: (index: number, field: keyof ItemRow, value: any) => void;
  onMaterialSelected: (index: number, material: MaterialResponse) => void;
  onClearMaterial: (index: number) => void;
  onCreateMaterial: (index: number, searchTerm: string) => void;
  uploadFile: (file: File) => Promise<PurchaseRequestFilePayload>;
  onUploadError?: (message: string) => void;
};

const SITE_OTHER = "__other__";

// De-dupe by name (two entries named "M") to avoid colliding Carbon SelectItem keys.
const unitNames = Array.from(
  new Map((MEASUREMENT_UNIT_LIST ?? []).map((u) => [u.name, u.name])).keys()
);

/**
 * The expanded per-item panel, rebuilt on Carbon inputs. The row lives in the page's RHF
 * `useFieldArray`; each field here commits through `onItemChange` (a typed `setValue` bridge) rather
 * than the string-only shared `TextField` primitive — deliberately, so a `number | null` quantity/
 * budget and an `{ id } | null` site reach the create payload in the exact shapes the inviolable
 * `POST /api/purchase-requests` contract expects. Material selection stays a bridge too
 * (`onMaterialSelected` patches many fields at once via `materialPatch`).
 */
const ItemDetail: React.FC<ItemDetailProps> = ({
  item,
  index,
  todayString,
  sites,
  onItemChange,
  onMaterialSelected,
  onClearMaterial,
  onCreateMaterial,
  uploadFile,
  onUploadError,
}) => {
  const isService = item.itemType === PurchaseRequestItemType.SERVICE;

  const [manualAddressOpen, setManualAddressOpen] = useState<boolean>(
    !item.site?.id && !!item.address
  );

  useEffect(() => {
    if (item.site?.id) setManualAddressOpen(false);
    else if (item.address) setManualAddressOpen(true);
  }, [item.site?.id, item.address]);

  const siteSelectValue = item.site?.id
    ? item.site.id
    : manualAddressOpen
      ? SITE_OTHER
      : "";

  const handleSiteSelectChange = (next: string) => {
    if (next === SITE_OTHER) {
      setManualAddressOpen(true);
      onItemChange(index, "site", null);
      return;
    }
    setManualAddressOpen(false);
    onItemChange(index, "site", next === "" ? null : { id: next });
    onItemChange(index, "address", "");
  };

  return (
    <div className="new-request-item__detail">
      <div className="new-request-item__detail-grid">
        {/* Identity: material, quantity, unit */}
        <div className="new-request-item__col">
          <div>
            <p className="cds--label">
              {i18next.t("purchaseRequests.items.columns.material")}
            </p>
            <MaterialAutocompleteCell
              value={item.materialName}
              isSelected={!!item.material?.id}
              onSelect={(material) => onMaterialSelected(index, material)}
              onClear={() => onClearMaterial(index)}
              onCreateNew={(searchTerm) => onCreateMaterial(index, searchTerm)}
            />
          </div>

          <div className="new-request-item__pair">
            <TextInput
              id={`item-${index}-quantity`}
              type="number"
              min={0}
              labelText={`${i18next.t("purchaseRequests.items.columns.quantity")} *`}
              value={item.quantity ?? ""}
              onChange={(e) =>
                onItemChange(
                  index,
                  "quantity",
                  e.target.value === "" ? null : Number(e.target.value)
                )
              }
            />
            <Select
              id={`item-${index}-unit`}
              labelText={i18next.t(
                "purchaseRequests.items.columns.measurementUnit"
              )}
              value={item.measurementUnit ?? ""}
              onChange={(e) =>
                onItemChange(
                  index,
                  "measurementUnit",
                  e.target.value === "" ? null : e.target.value
                )
              }
            >
              <SelectItem
                value=""
                text={i18next.t("purchaseRequests.items.unitPlaceholder")}
              />
              {unitNames.map((name) => (
                <SelectItem key={name} value={name} text={name} />
              ))}
            </Select>
          </div>

          {!isService && (
            <TextArea
              id={`item-${index}-description`}
              rows={2}
              labelText={i18next.t(
                "purchaseRequests.items.columns.description"
              )}
              value={item.description || ""}
              onChange={(e) =>
                onItemChange(index, "description", e.target.value)
              }
            />
          )}

          {isService && (
            <>
              <TextArea
                id={`item-${index}-purpose`}
                rows={2}
                labelText={`${i18next.t("purchaseRequests.items.columns.purpose")} *`}
                placeholder={i18next.t(
                  "purchaseRequests.items.purposePlaceholder"
                )}
                maxLength={1000}
                value={item.purpose || ""}
                onChange={(e) => onItemChange(index, "purpose", e.target.value)}
              />
              <TextArea
                id={`item-${index}-application`}
                rows={2}
                labelText={i18next.t(
                  "purchaseRequests.items.columns.application"
                )}
                placeholder={i18next.t(
                  "purchaseRequests.items.applicationPlaceholder"
                )}
                maxLength={1000}
                value={item.brand || ""}
                onChange={(e) => onItemChange(index, "brand", e.target.value)}
              />
            </>
          )}
        </div>

        {/* Delivery, and the per-item overrides the options panel didn't take */}
        <div className="new-request-item__col">
          <div>
            <Select
              id={`item-${index}-site`}
              labelText={`${i18next.t("purchaseRequests.items.columns.site")} *`}
              value={siteSelectValue}
              onChange={(e) => handleSiteSelectChange(e.target.value)}
            >
              <SelectItem
                value=""
                text={i18next.t("purchaseRequests.items.selectSite")}
              />
              {sites.map((s) => (
                <SelectItem key={s.id} value={s.id} text={s.name} />
              ))}
              <SelectItem
                value={SITE_OTHER}
                text={i18next.t("purchaseRequests.items.siteOther")}
              />
            </Select>
            {manualAddressOpen && (
              <TextInput
                id={`item-${index}-address`}
                labelText={i18next.t("purchaseRequests.items.siteOther")}
                hideLabel
                placeholder={i18next.t(
                  "purchaseRequests.items.manualAddressPlaceholder"
                )}
                helperText={i18next.t(
                  "purchaseRequests.items.manualAddressHint"
                )}
                value={item.address || ""}
                onChange={(e) => onItemChange(index, "address", e.target.value)}
              />
            )}
          </div>

          {/* Per-item date + budget overrides. These ALWAYS render: leaving them
              untouched keeps the request-level default (and the greyed "inherited"
              look in the items table); filling them overrides just this item. */}
          <div className="new-request-item__pair">
            <div className="new-request-date">
              <label
                htmlFor={`item-${index}-neededFrom`}
                className="cds--label"
              >
                {i18next.t("purchaseRequests.items.columns.neededFrom")}
              </label>
              <input
                id={`item-${index}-neededFrom`}
                type="date"
                className="new-request-date__input"
                value={item.neededFrom ?? ""}
                min={todayString}
                onChange={(e) =>
                  onItemChange(index, "neededFrom", e.target.value || null)
                }
              />
            </div>
            <div className="new-request-date">
              <label htmlFor={`item-${index}-neededTo`} className="cds--label">
                {i18next.t("purchaseRequests.items.columns.neededTo")}
              </label>
              <input
                id={`item-${index}-neededTo`}
                type="date"
                className="new-request-date__input"
                value={item.neededTo ?? ""}
                min={item.neededFrom ?? todayString}
                onChange={(e) =>
                  onItemChange(index, "neededTo", e.target.value || null)
                }
              />
            </div>
          </div>

          <div className="new-request-item__pair new-request-item__pair--budget">
            <Select
              id={`item-${index}-currency`}
              labelText={i18next.t("purchaseRequests.budgetType.currency")}
              value={item.currency || Currency.USD}
              onChange={(e) => {
                const val = e.target.value as Currency;
                onItemChange(index, "currency", val);
                onItemChange(index, "budgetCurrency", val);
              }}
            >
              {CURRENCY_OPTIONS.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  text={opt.label}
                />
              ))}
            </Select>
            <TextInput
              id={`item-${index}-budgetValue`}
              type="number"
              labelText={i18next.t(
                "purchaseRequests.items.columns.budgetValue"
              )}
              value={item.budgetValue ?? ""}
              onChange={(e) =>
                onItemChange(
                  index,
                  "budgetValue",
                  e.target.value === "" ? null : Number(e.target.value)
                )
              }
            />
          </div>

          {item.materialOwnerName && item.materialOwnerName !== "-" && (
            <p className="new-request-item__owner">
              {i18next.t("purchaseRequests.columns.assignedBuyer")}:{" "}
              <span>{item.materialOwnerName}</span>
            </p>
          )}
        </div>
      </div>

      <ItemFilePicker
        files={item.files ?? []}
        uploadFile={uploadFile}
        onChange={(next) => onItemChange(index, "files", next)}
        onError={onUploadError}
      />
    </div>
  );
};

export default ItemDetail;
